import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { getVehicles } from 'Services/api/vehicles';
import { useListType } from 'Stores/ListStore/ListStore';
import { useGetMakesBySlugs } from 'Stores/MakesStore/MakesStore';
import { useGetModelsById } from 'Stores/ModelsStore/ModelsStore';
import type { Filter } from 'Types/filter';
import { formatFiltersToQuery } from 'Utilities/filterQuery/formatFiltersToQuery';
import useSortOrder from 'Utilities/hooks/vehicleList/useSortOrder/useSortOrder';
import { getMakeModelDictionary } from 'Utilities/makeModel/makeModel.helpers';

const DEBOUNCE_MS = 300;

interface UseFilterCountPreviewOptions {
	enabled?: boolean;
	filter?: Partial<Filter>;
}

interface UseFilterCountPreviewReturn {
	count?: number;
	isFetching: boolean;
	isLoading: boolean;
}

/**
 * Debounced, cached vehicle count preview. Resolves the make/model dictionary
 * internally and fetches via React Query keyed on the serialized server query.
 */
export const useFilterCountPreview = ({
	enabled = true,
	filter,
}: UseFilterCountPreviewOptions): UseFilterCountPreviewReturn => {
	const getMakesBySlugs = useGetMakesBySlugs();
	const getModelsById = useGetModelsById();
	const { defaultOrder, defaultSort } = useSortOrder();
	const listType = useListType();

	const [debouncedFilter, setDebouncedFilter] = useState(filter);
	const [isDebouncing, setIsDebouncing] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isFirstChange = useRef(true);

	useEffect(() => {
		if (!enabled) {
			return undefined;
		}

		if (isFirstChange.current) {
			isFirstChange.current = false;
			setDebouncedFilter(filter);
			return undefined;
		}

		if (debounceRef.current) clearTimeout(debounceRef.current);
		setIsDebouncing(true);
		debounceRef.current = setTimeout(() => {
			setDebouncedFilter(filter);
			setIsDebouncing(false);
		}, DEBOUNCE_MS);

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [enabled, filter]);

	useEffect(() => {
		if (!enabled) {
			setDebouncedFilter(undefined);
		}
	}, [enabled]);

	const serverQuery = useMemo(() => {
		if (!debouncedFilter || !enabled) return undefined;

		const selectedMakes = getMakesBySlugs(debouncedFilter?.make);
		const selectedModels = getModelsById((debouncedFilter?.model ?? []) as number[]);
		const makeAndModel = getMakeModelDictionary({ selectedMakes, selectedModels });
		const { serverQuery: sq } = formatFiltersToQuery({
			defaultOrder,
			defaultSort,
			filters: { ...debouncedFilter, listType } as Parameters<typeof formatFiltersToQuery>[0]['filters'],
			makeAndModel,
		});
		return sq;
	}, [debouncedFilter, defaultOrder, defaultSort, enabled, getMakesBySlugs, getModelsById, listType]);

	const { data, isFetching, isLoading } = useQuery({
		enabled: enabled && serverQuery !== undefined,
		keepPreviousData: true,
		queryFn: async ({ signal }) => {
			const result = await getVehicles({ filterQuery: serverQuery! }, signal);
			return result.totalCount;
		},
		queryKey: ['filterCountPreview', serverQuery],
		staleTime: 30_000,
	});

	return {
		count: data,
		isFetching: isDebouncing || isFetching,
		isLoading,
	};
};

/**
 * Imperative variant for use cases where the filter is set via callback
 * rather than being known at mount time (e.g. selecting a saved search).
 */
export const useFilterCountPreviewImperative = (): {
	count?: number;
	fetchCount: (filter: Partial<Filter>) => void;
	isFetching: boolean;
	isLoading: boolean;
	reset: () => void;
} => {
	const [filter, setFilter] = useState<Partial<Filter> | undefined>(undefined);
	const [isEnabled, setIsEnabled] = useState(false);

	const { count: queryCount, isFetching, isLoading } = useFilterCountPreview({ enabled: isEnabled, filter });

	const fetchCount = useCallback((f: Partial<Filter>) => {
		setFilter(f);
		setIsEnabled(true);
	}, []);

	const reset = useCallback(() => {
		setFilter(undefined);
		setIsEnabled(false);
	}, []);

	return { count: isEnabled ? queryCount : undefined, fetchCount, isFetching, isLoading, reset };
};
